import { lazy, Suspense } from "react";
import { motion } from "framer-motion";
import styled, { ThemeProvider } from "styled-components";
import { DarkTheme, mediaQueries } from "./Themes";
import Loading from "../subComponents/Loading";
import Seo from "./Seo";
import RelatedPages from "./RelatedPages";
import { pages, navLinks, stats } from "../data/site";
import {
  breadcrumbSchema,
  buildGraph,
  webpageSchema,
} from "../data/schema";

const SocialIcons = lazy(() => import("../subComponents/SocialIcons"));
const PowerButton = lazy(() => import("../subComponents/PowerButton"));
const LogoComponent = lazy(() => import("../subComponents/LogoComponent"));
const ParticlesComponent = lazy(() =>
  import("../subComponents/ParticleComponent")
);

const Page = styled(motion.div)`
  background-color: ${(props) => props.theme.body};
  width: 100vw;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  box-sizing: border-box;
  padding: 6rem 0 6rem;
`;

const Title = styled.div`
  position: relative;
  z-index: 1;
  width: 50vw;
  margin: 0 auto 1.5rem;
  color: ${(props) => `rgba(${props.theme.textRgba}, 0.2)`};
  font-size: calc(3rem + 5vw);
  line-height: 1;

  ${mediaQueries(40)`
    width: 60vw;
  `};

  ${mediaQueries(30)`
    width: auto;
    margin: 0 1rem 1rem 4.5rem;
  `};
`;

const Stack = styled(motion.div)`
  width: 50vw;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  position: relative;
  z-index: 3;

  ${mediaQueries(40)`
    width: 60vw;
  `};

  ${mediaQueries(30)`
    width: auto;
    margin: 0 1rem 0 4.5rem;
  `};
`;

const Card = styled(motion.article)`
  border: 2px solid ${(props) => props.theme.text};
  color: ${(props) => props.theme.text};
  padding: 1.6rem 2rem;
  line-height: 1.5;
  font-size: calc(0.5rem + 1vw);
  backdrop-filter: blur(4px);
  background: rgb(0 0 0 / 0%);
  font-family: "Ubuntu Mono", monospace;
  font-style: italic;
  transition: background-color 0.25s ease, color 0.25s ease;

  h2 {
    display: block;
    font-family: "Karla", sans-serif;
    font-style: normal;
    font-weight: 600;
    margin: 0 0 0.6rem;
    font-size: 1.05em;
  }

  p,
  ul {
    margin: 0 0 0.7rem;
  }

  ul:last-child,
  p:last-child {
    margin-bottom: 0;
  }

  ul {
    margin-left: 1.2rem;
  }

  a {
    color: inherit;
  }

  &:hover {
    color: ${(props) => props.theme.body};
    background-color: ${(props) => props.theme.text};
  }

  ${mediaQueries(40)`
    font-size: calc(0.7rem + 0.8vw);
  `};

  ${mediaQueries(30)`
    padding: 1.3rem;
    font-size: 0.95rem;
  `};
`;

const Stats = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem 1rem;

  li {
    margin: 0;
  }

  strong {
    display: block;
    font-family: "Karla", sans-serif;
    font-style: normal;
    font-size: 1.15em;
  }

  ${mediaQueries(30)`
    grid-template-columns: 1fr;
  `};
`;

const SrOnly = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`;

const list = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.2 },
  },
};

const rise = {
  hidden: { opacity: 0, y: 28 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", duration: 0.7 },
  },
};

const ExperiencePage = () => {
  const seo = pages.experience;
  const schema = buildGraph([
    webpageSchema(seo),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Experience", path: seo.path },
    ]),
  ]);

  return (
    <ThemeProvider theme={DarkTheme}>
      <Seo
        title={seo.title}
        description={seo.description}
        path={seo.path}
        schema={schema}
      />
      <Suspense fallback={<Loading />}>
        <Page
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.5 } }}
          exit={{ opacity: 0, transition: { duration: 0.5 } }}
        >
          <LogoComponent theme="dark" />
          <PowerButton />
          <SocialIcons theme="dark" />
          <ParticlesComponent theme="dark" />
          <SrOnly>Experience — Hareesh Rajendran</SrOnly>
          <Title aria-hidden="true">WORK</Title>
          <Stack variants={list} initial="hidden" animate="show">
            <Card variants={rise}>
              <h2>Hareesh Rajendran</h2>
              <p>
                Founder and CEO of Hayan Tech. Previously built products at
                Highbrow Technology and Zoho, and now focuses on Vanakkam DSA,
                Tamil technology education, and AI-assisted software.
              </p>
            </Card>

            <Card variants={rise}>
              <h2>What I work on</h2>
              <p>
                AI Agents, Agentic AI, System Design, software engineering,
                developer tools, DSA and interview preparation, automation, and
                AI-assisted product development.
              </p>
            </Card>

            <Card variants={rise}>
              <h2>Numbers</h2>
              <Stats>
                {stats.map((item) => (
                  <li key={item.label}>
                    <strong>{item.value}</strong>
                    {item.label}
                  </li>
                ))}
              </Stats>
            </Card>

            <Card variants={rise}>
              <h2>Founder and CEO, Hayan Tech</h2>
              <p>June 2026 – Present · Chennai, India</p>
              <ul>
                <li>
                  Built Vanakkam DSA from zero as a Tamil cohort-based DSA and
                  interview preparation platform.
                </li>
                <li>Launched and scaled 7 cohorts, with 1000+ paying students.</li>
                <li>
                  Average order value grew over 100% compared with Cohort I.
                </li>
                <li>
                  Built the full CRM stack on Zoho, including pipelines, lead
                  stages, webhooks, round-robin assignment and lead capture.
                </li>
                <li>
                  Unified WhatsApp, Instagram and website leads into one funnel.
                </li>
                <li>
                  Integrated AiSensy for WhatsApp automation and FreJun for
                  outbound calling.
                </li>
                <li>
                  Built AI-assisted lead replies, FAQ handling, follow-ups, and
                  n8n agentic workflows.
                </li>
                <li>
                  Automated lead qualification, support resolution, booking
                  reminders with Firebase Functions, and job alerts backed by a
                  PostgreSQL retry queue.
                </li>
                <li>
                  Hired and trained the sales team, and runs daily KPI-focused
                  standups and call-script reviews.
                </li>
                <li>
                  Owns the complete funnel from YouTube to lead capture,
                  nurture, sales, payment and onboarding.
                </li>
                <li>
                  Produces long-form Tamil content on System Design, AI Agents
                  and Agentic AI.
                </li>
                <li>
                  Handles brand collaborations, GST compliance, invoicing and
                  P&amp;L.
                </li>
              </ul>
            </Card>

            <Card variants={rise}>
              <h2>Product Engineer, Highbrow Technology</h2>
              <p>January 2026 – June 2026</p>
              <ul>
                <li>
                  Owned the product roadmap for Cleverhire, an AI-assisted
                  recruitment platform.
                </li>
                <li>
                  Worked on ATS, an autonomous AI interviewer, recruiter
                  copilot and anti-cheating capabilities.
                </li>
                <li>Wrote PRDs, user stories and acceptance criteria.</li>
                <li>
                  Led go-to-market positioning and early pricing and customer
                  launches.
                </li>
                <li>
                  Conducted customer discovery with recruiters and hiring teams.
                </li>
                <li>Ran sprint planning, grooming and release coordination.</li>
                <li>Introduced AI-driven automation into product development.</li>
              </ul>
            </Card>

            <Card variants={rise}>
              <h2>Senior Software Engineer, Highbrow Technology</h2>
              <p>December 2024 – June 2026</p>
              <ul>
                <li>Early engineering team member and owner of frontend systems.</li>
                <li>Co-built Cleverhire and designed database schemas using DBML.</li>
                <li>Established engineering practices across the team.</li>
                <li>
                  Led frontend architecture across Cleverhire, TalentBridge and
                  Timesheet Tracker.
                </li>
                <li>
                  Evaluated 15+ UI libraries and standardized on Magic UI,
                  Accentricity UI and Shadcn.
                </li>
                <li>
                  Shipped Timesheet Tracker in two weeks and grew it to 500+
                  users.
                </li>
                <li>
                  Introduced AI-assisted development, validated Cursor before
                  team adoption, and experimented with multiple LLMs and 10–20
                  voice models.
                </li>
                <li>
                  Built digital signature workflows, asynchronous processing and
                  bulk operations.
                </li>
              </ul>
            </Card>

            <Card variants={rise}>
              <h2>Member of Technical Staff, Zoho</h2>
              <p>June 2023 – November 2024</p>
              <ul>
                <li>
                  Full-stack product development across GraphQL, microservices
                  and serverless architecture.
                </li>
                <li>
                  Worked on multi-tenancy, thread pooling, Apollo GraphQL and
                  Redis ORM.
                </li>
                <li>Product Owner of Zgraph at Zoho Catalyst and MBaaS.</li>
              </ul>
            </Card>

            <Card variants={rise}>
              <h2>Project Trainee, Zoho</h2>
              <p>July 2022 – June 2023</p>
              <ul>
                <li>
                  Worked with GraphQL, service-oriented architecture, ORM,
                  serverless and cloud infrastructure.
                </li>
                <li>
                  Built with REST APIs, Redis, MySQL, Java, JavaScript,
                  Handlebars and Highcharts.
                </li>
              </ul>
            </Card>

            <Card variants={rise}>
              <h2>Internships and programs</h2>
              <ul>
                <li>
                  Mentee, Amazon ML Summer School · June 2022 – August 2022 ·
                  Machine Learning, Data Science and Data Analysis.
                </li>
                <li>
                  Summer Intern, Catalyst by Zoho · March 2022 – April 2022 ·
                  SQL, JDBC, Java and Kafka.
                </li>
                <li>
                  Frontend Web Developer, Bit Brothers · August 2021 –
                  September 2021 · React, Material UI, Figma and GitHub.
                </li>
                <li>
                  Frontend Web Developer, Mineral Drop · November 2020 – March
                  2021 · HTML, CSS, Bootstrap and Firebase.
                </li>
                <li>
                  Web Developer, Fashioo · October 2020 – November 2020 · HTML,
                  CSS, Bootstrap and JavaScript.
                </li>
              </ul>
            </Card>

            <Card variants={rise}>
              <h2>Co-founder, Technamite</h2>
              <p>Inactive venture.</p>
            </Card>

            <Card variants={rise}>
              <h2>Education</h2>
              <p>
                BE Computer Science, RMK Engineering College, 2023. During
                college, Hareesh managed Hiren, a 15,000-member community.
              </p>
            </Card>

            <Card variants={rise}>
              <RelatedPages
                links={navLinks.filter((link) => link.to !== seo.path)}
              />
            </Card>
          </Stack>
        </Page>
      </Suspense>
    </ThemeProvider>
  );
};

export default ExperiencePage;
